import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AUTH_ME_URL, AuthService } from '@services/auth';
import { ResellerToastService } from '@services/toast.service';
import { networkErrorKey, parseApiError } from '@utils/api-error-message';
import { I18NextPipe } from 'angular-i18next';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

export const MANUAL_ERROR_HANDLED = 'MANUAL_ERROR_HANDLED';

const SUPPRESSED_STATUSES = [401, 420];

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const toastService = inject(ResellerToastService);
  const i18n = inject(I18NextPipe);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => resolveBlobError(err)),
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 && !req.url.includes(AUTH_ME_URL)) {
        auth.invalidate();
        router.navigateByUrl(auth.logoutUrlTree(router));
      }

      if (!isSuppressed(req, err)) {
        toastService.apiError(parseApiError(err), { url: err.url, status: err.status });
      }

      const body =
        err.error && typeof err.error === 'object' && 'errors' in err.error
          ? err.error
          : { errors: { network: [i18n.transform(networkErrorKey)] } };

      return throwError(() => ({ ...body, status: err.status }));
    })
  );
};

function isSuppressed(req: HttpRequest<unknown>, err: HttpErrorResponse): boolean {
  return SUPPRESSED_STATUSES.includes(err.status) || req.headers.has(MANUAL_ERROR_HANDLED);
}

// responseType 'blob' requests (invoice/receipt download) get their error body undecoded too,
// so it arrives here as a Blob instead of the parsed JSON the backend actually sent
function resolveBlobError(error: HttpErrorResponse): Observable<never> {
  if (!(error.error instanceof Blob)) {
    return throwError(() => error);
  }

  return from(error.error.text()).pipe(
    switchMap((text) =>
      throwError(
        () =>
          new HttpErrorResponse({
            error: parseBlobText(text),
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url
          })
      )
    )
  );
}

function parseBlobText(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
